import React, { useRef, useMemo, forwardRef, useCallback } from 'react';
import _, { isEmpty, findIndex, forEach, split, uniqBy } from 'lodash';
import { Form } from 'antd'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes, parseSelectOptions } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import useLocalPagination from 'hooks/useLocalPagination';

const integrationTypes = [
    {
        label: 'Tag',
        value: 'tag'
    },
    {
        label: 'Host',
        value: 'host'
    },
    {
        label: "Network",
        value: "network"
    }
];


const QualysInfraMappingForm = forwardRef(({ disableProjectFields = false, ...props }, ref) => {
    const [form] = Form.useForm();
    const projectRef = useRef({ host: [], tag: [], network: [] });
    const selectedNames = useRef([]);
    const IntRef = useRef({})

    const fetchProjects = useCallback((type) => {
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, type });
        promise.then((response) => {
            forEach(response.projects, p => {
                projectRef.current[type][p.id] = p.name;
            });
        });
        return { promise, cancel };

    }, [props.toolName, props.loginConfigId]);

    const fetchTags = useCallback((params) => {
        const { loginConfigId, type } = params;
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: loginConfigId, type: type, ...params });
        promise.then((response) => {
            forEach(response.projects, p => {
                projectRef.current[type][p.id] = p.name;
            });
        });
        return { promise, cancel };

    }, [props.toolName])

    const fetchHosts = useCallback(() => fetchProjects("host"), [fetchProjects]);

    const fetchNetworks = useCallback(() => fetchProjects("network"), [fetchProjects])

    const {getPageData: getHostPageData} = useLocalPagination(fetchHosts);
    const { getPageData: getNetworksPageData } = useLocalPagination(fetchNetworks);

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
        const { hostId, hostName, tagId, tagName, networkId, networkName } = config;
        const refIdArr = [tagId, hostId, networkId];
        const validIdIndex = findIndex(refIdArr, (value) => !isEmpty(value));
        //keeping the index as 0 as we show only one configuration on edit 
        //as the configurations gets saved individually for multiple mappings too
        selectedNames.current[0] = tagName ? split(tagName, ",") : hostName ? split(hostName, ",") : networkName ? split(networkName, ",") : "";
        const projectId = refIdArr[validIdIndex] === "All" ? undefined :  split(refIdArr[validIdIndex], ",");

        return ({
            projectId,
            integration: integrationTypes[validIdIndex]?.value,
            ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
        });
    }, []);

    const onIntegrationTypeChange = useCallback((value, field) => {
        const { name } = field;
        const configurations = form.getFieldValue("configurations") || [];
        let updatedConfiguration = [
            ...configurations,
        ];

        updatedConfiguration[name] = {
            ...updatedConfiguration[name],
            integration: value,
            projectId: undefined
        }

        form.setFieldsValue({ configurations: updatedConfiguration });
    }, [form]);

    const getExtraFieldsData = useCallback((config, index) => {
        const { projectId, integration, extraParams } = config;
        const extraFieldData = { extraParams, ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id }) };
        //handling multiple mapping case using index
        //to prevent wrong name map when multiple configurations are saved
        const configSelectedNames = selectedNames.current[index]
        const isAll = isEmpty(projectId);
        switch (integration) {
            case 'host':
                extraFieldData.hostId = isAll  ? "All" : projectId.join();
                extraFieldData.hostName = isAll ? "All" : configSelectedNames.join();
                break;
            case 'tag':
                extraFieldData.tagId = isAll ? "All" : projectId.join();
                extraFieldData.tagName = isAll ? "All" : configSelectedNames.join();
                break;
            case "network":
                extraFieldData.networkId = isAll ? "All" : projectId.join();
                extraFieldData.networkName = isAll ? "All" : configSelectedNames.join();
                extraFieldData.networkIdWithName = isAll ? undefined : projectId.map((networkID, index) => `${networkID}|:${configSelectedNames[index]}`).join();
                break;
            default:
                extraFieldData.hostId = isAll ? "All" : projectId.join();
                extraFieldData.hostName = isAll ? "All" : configSelectedNames.join();
                extraFieldData.tagId = isAll? "All" : projectId.join();
                extraFieldData.tagName = isAll ? "All" : configSelectedNames.join();
                break;
        }
        return extraFieldData
    }, []);

    const isProjectFieldDisabled = useCallback((fieldKey, form) => {
        const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
        return (!fieldConfiguration || !fieldConfiguration.integration);
    }, []);


    const extraFields = useMemo(() => ([
        {
            name: 'integration',
            colSpan: '10',
            type: FieldTypes.SELECT,
            label: 'Integration Type',
            options: integrationTypes,
            disabled: disableProjectFields,
            required: false,
            placeholder:  "Leave blank to fetch all findings",
            props: {
                onChange: onIntegrationTypeChange,
                autoFocus: !disableProjectFields
            }
        },
        {
            name: 'projectId',
            colSpan: '10',
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: 'Qualys Infrastructure Host/Tag/Network',
            propsGenerator: (fieldKey) => ({ disabled: isProjectFieldDisabled(fieldKey, form) || disableProjectFields }),
            required: false,
            placeholder: "Leave blank to fetch all findings",
            disabled: disableProjectFields,
            props: {
                onChange: (value, field, selectedOptions) => {
                    //handling multiple mapping case using index
                    selectedNames.current[field.name] = _.map(selectedOptions, "name");
                },
                setExtraProps: (field, listField) => {
                    const allConfigs = form.getFieldsValue().configurations;
                    const type = allConfigs[listField.name]?.integration;
                    if (!IntRef.current[type]) {
                        IntRef.current[type] = { login_id: props.loginConfigId, type };
                    }
                    return {
                        api: type ? type === "host" ? getHostPageData : type === "tag" ? fetchTags : getNetworksPageData : undefined,
                        apiParams: IntRef.current[type],
                        resetOptionsProp: type,
                        mode: "multiple",
                        allowClear: true,
                    }
                },
                parseSelectOptions: (options) => {
                        //if option is not selected from first page
                        //we need to append our selected host/tag on edit
                        //to avoid displaying id instead of name
                        if(props.record){
                           const {
                            tagId,
                            tagName,
                            hostId,
                            hostName,
                            networkId,
                            networkName,
                            } = props.record;
                            const recordOptions = !isEmpty(hostId)
                            ? getOptions(hostId, hostName)
                            : !isEmpty(tagId)
                                ? getOptions(tagId, tagName)
                                : !isEmpty(networkId)
                                ? getOptions(networkId, networkName)
                                : [];
                                
                            return parseSelectOptions(uniqBy([...options, ...(recordOptions)], "id"))
                        }
                        return parseSelectOptions(options)
                }
            }
        },
        {
            name: 'extraParams',
            colSpan: '10',
            type: FieldTypes.ANTD_DEFAULT_INPUT,
            help: <>Enter URL Query params as (a=1&b=2). Following params are not allowed output_format,vm_processed_after, for more information on extra param please refer to 'Host List Detection' from page 756 in below doc <a href="https://cdn2.qualys.com/docs/qualys-api-vmpc-user-guide.pdf" target="_blank" rel="noreferrer">(learn more)</a></>,
            label: 'Extra Params'
        }
    ]), [
        disableProjectFields,
        onIntegrationTypeChange,
        isProjectFieldDisabled,
        form,
        getHostPageData,
        fetchTags,
        props.loginConfigId,
        props.record,
        getNetworksPageData,
      ]);


    return (
        <GenericMappingForm
            form={form}
            ref={ref}
            getExtraFieldsData={getExtraFieldsData}
            extraFields={extraFields}
            parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
            {...props}
        />
    )
})

export default QualysInfraMappingForm;

export const getOptions = (idString, nameString) => split(idString, ",").map((id, index) => ({id, name: split(nameString, ",")[index]}))

